import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Lock, Mail } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import type { LoginCredentials } from '../../types'

interface LoginFormProps {
  onSubmit: (credentials: LoginCredentials) => Promise<boolean>
  error?: string | null
  isLoading?: boolean
}

export function LoginForm({ onSubmit, error, isLoading }: LoginFormProps) {
  const { t } = useTranslation()

  const schema = z.object({
    email: z.string().email(t('admin.login.errors.email')),
    password: z.string().min(6, t('admin.login.errors.password')),
  })

  type FormData = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <Card variant="gradient" className="w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
            <Lock className="h-6 w-6 text-accent-light" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-text-primary">
            {t('admin.login.title')}
          </h1>
          <p className="mt-2 text-text-secondary">{t('admin.login.subtitle')}</p>
        </div>

        <form
          onSubmit={handleSubmit(async (data) => {
            await onSubmit(data)
          })}
          className="mt-8 space-y-5"
        >
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium text-text-primary">
              {t('admin.login.email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted" />
              <input
                id="email"
                type="email"
                {...register('email')}
                className="w-full rounded-lg border border-border-subtle bg-bg py-3 pl-10 pr-4 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                placeholder="admin@empleahoy.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-error">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-medium text-text-primary">
              {t('admin.login.password')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted" />
              <input
                id="password"
                type="password"
                {...register('password')}
                className="w-full rounded-lg border border-border-subtle bg-bg py-3 pl-10 pr-4 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                placeholder="••••••••"
              />
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-error">{errors.password.message}</p>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
              {error}
            </div>
          )}

          <Button type="submit" isLoading={isLoading} className="w-full">
            {t('admin.login.submit')}
          </Button>
        </form>
      </Card>
    </div>
  )
}
